import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo, faqList } from '@/mocks/about';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim()) {
      setFormStatus('success');
      return;
    }
    formData.delete('company_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9rg5ennahb7382ovm3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string } } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(parsed?.meta?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于快猫短视频',
    description: aboutInfo.story,
    url: 'https://www.kuaimao.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '快猫短视频',
      url: 'https://www.kuaimao.com',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb />

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">关于快猫短视频</h1>
              <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                {aboutInfo.slogan}
              </p>
            </div>

            <div className="bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-10 mb-12">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {aboutInfo.story}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
              {aboutInfo.values.map((val, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 flex gap-4">
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-secondary-50 flex items-center justify-center">
                    <i className={`${val.icon} text-xl text-secondary-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-800 mb-1">{val.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">创作者口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-semibold text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</div>
                    <a href={`mailto:${aboutInfo.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                      {aboutInfo.contact.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-phone-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">客服电话</div>
                    <span className="text-sm text-foreground-500">{aboutInfo.contact.phone}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-map-pin-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">公司地址</div>
                    <span className="text-sm text-foreground-500">{aboutInfo.contact.address}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-primary-50 flex items-center justify-center">
                    <i className="ri-time-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800 mb-1">工作时间</div>
                    <span className="text-sm text-foreground-500">{aboutInfo.contact.workingHours}</span>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 md:p-8">
                <h3 className="text-base font-semibold text-foreground-800 mb-5">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名 *</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱 *</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言内容 *</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入你想说的话（最多500字）"
                    ></textarea>
                  </div>
                  <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="antispam-field" />
                  <button
                    type="submit"
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600">消息已发送成功！我们会尽快回复你。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500">{formError || '发送失败，请稍后重试'}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}